// src/components/Gallery.js
import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";
import "yet-another-react-lightbox/plugins/thumbnails.css";
import SectionIntro from "./SectionIntro";

const galleryData = {
  "Exterior & Views": [
    "/images/gallery/exterior1.jpg",
    "/images/gallery/exterior2.jpg",
    "/images/gallery/ext3.jpg",
    "/images/gallery/exterior4.jpg",
    "/images/gallery/exterior5.jpg",
    "/images/gallery/exterior6.jpg",
  ],
  "Bedrooms & Suites": [
    "/images/gallery/living-room-1.jpg",
    "/images/gallery/living2.jpg",
    "/images/gallery/living3.jpg",
    "/images/gallery/living4.jpg",
    "/images/gallery/living5.jpg",
    "/images/gallery/living66.jpg",
  ],
  "Living Areas": [
    "/images/gallery/livingroom1.jpg",
    "/images/gallery/livingroom2.jpg",
    "/images/gallery/livingroom3.jpg",
    "/images/gallery/livingroom4.jpg",
  ],
  "Bathrooms & Showers": [
    "/images/gallery/bathroom.jpg",
    "/images/gallery/photo3.jpg",
    "/images/gallery/bathroom3.jpg",
  ],
  "Sauna (traditionnal hamam)": [
    "/images/gallery/sauna1.jpg",
    "/images/gallery/sauna22.jpg",
    "/images/gallery/sauna3.jpg",
  ],
};

const Gallery = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("Exterior & Views");
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [visibleCategories, setVisibleCategories] = useState(
    new Set(["Exterior & Views"])
  );

  const categoryRefs = useRef({});
  const tabs = Object.keys(galleryData);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const category = entry.target.dataset.category;
          if (entry.isIntersecting) {
            setVisibleCategories((prev) => new Set([...prev, category]));
            setActiveTab(category);
          }
        });
      },
      { threshold: 0.3, rootMargin: "-10% 0px -10% 0px" }
    );

    Object.values(categoryRefs.current).forEach((ref) => {
      if (ref) observer.observe(ref);
    });
    return () => observer.disconnect();
  }, []);

  const scrollToCategory = (category) => {
    categoryRefs.current[category]?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
    setActiveTab(category);
  };

  const openLightbox = (category, index) => {
    let globalIndex = 0;
    for (let i = 0; i < tabs.indexOf(category); i++) {
      globalIndex += galleryData[tabs[i]].length;
    }
    globalIndex += index;
    setLightboxIndex(globalIndex);
    setLightboxOpen(true);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.08 } },
  };
  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
    },
  };

  const allSlides = tabs.flatMap((category) =>
    galleryData[category].map((src) => ({ src, alt: `${category} image` }))
  );

  return (
    <section className="min-h-screen bg-cream pt-32 md:pt-40 pb-24">
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <SectionIntro
          eyebrow={t("labels.gallery")}
          title={t("galleryy.title")}
          lead={t("galleryy.subtitle")}
          className="mb-16 md:mb-20"
        />

        <div className="flex flex-col lg:flex-row gap-10">
          {/* Sidebar nav */}
          <div className="hidden lg:block lg:w-60 flex-shrink-0">
            <div className="sticky top-28">
              <h3 className="eyebrow text-ink-muted mb-6 pb-4 border-b border-ink/10">
                {t("nav.gallery")}
              </h3>
              <nav className="space-y-1">
                {tabs.map((tab) => (
                  <button
                    key={tab}
                    onClick={() => scrollToCategory(tab)}
                    className={`w-full text-left py-3 transition-colors duration-300 flex items-center justify-between group ${
                      activeTab === tab
                        ? "text-ink"
                        : "text-ink-muted hover:text-ink"
                    }`}
                  >
                    <span className="text-sm tracking-wide">{tab}</span>
                    <span
                      className={`w-1.5 h-1.5 rounded-full transition-all ${
                        activeTab === tab
                          ? "bg-brass scale-100"
                          : "bg-ink/20 scale-75 group-hover:bg-brass/50"
                      }`}
                    />
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Gallery */}
          <div className="flex-1">
            <div className="space-y-20 md:space-y-28">
              {tabs.map((category) => (
                <motion.section
                  key={category}
                  ref={(el) => (categoryRefs.current[category] = el)}
                  data-category={category}
                  className="scroll-mt-28"
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.8 }}
                >
                  <div className="mb-10">
                    <h3 className="font-serif font-light text-3xl md:text-4xl text-ink">
                      {category}
                    </h3>
                    <div className="rule-gold mt-4" />
                  </div>

                  <motion.div
                    className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4"
                    variants={containerVariants}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-50px" }}
                  >
                    {galleryData[category].map((src, index) => (
                      <motion.div
                        key={`${category}-${index}`}
                        className="group relative overflow-hidden bg-cream-dark cursor-pointer aspect-[4/3]"
                        variants={itemVariants}
                        onClick={() => openLightbox(category, index)}
                      >
                        <img
                          src={src}
                          alt={`${category} ${index + 1}`}
                          className="w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-110"
                          loading="lazy"
                          decoding="async"
                        />
                        <div className="absolute inset-0 bg-ink/0 group-hover:bg-ink/25 transition-colors duration-500" />
                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                          <span className="w-12 h-12 border border-cream/70 rounded-full flex items-center justify-center text-cream">
                            <svg
                              className="w-5 h-5"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={1.5}
                                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                              />
                            </svg>
                          </span>
                        </div>
                      </motion.div>
                    ))}
                  </motion.div>
                </motion.section>
              ))}
            </div>
          </div>
        </div>
      </div>

      <Lightbox
        open={lightboxOpen}
        index={lightboxIndex}
        close={() => setLightboxOpen(false)}
        slides={allSlides}
        plugins={[Zoom, Thumbnails]}
        zoom={{ maxZoomPixelRatio: 5, scrollToZoom: true }}
        thumbnails={{ position: "bottom", width: 100, height: 80, borderRadius: 4, padding: 4 }}
        carousel={{ finite: false, preload: 2, imageFit: "contain" }}
        controller={{ closeOnBackdropClick: true, closeOnPullDown: true }}
        styles={{
          container: { backgroundColor: "rgba(28, 27, 24, 0.96)" },
          root: {
            "--yarl__color_backdrop": "rgba(28, 27, 24, 0.96)",
            "--yarl__thumbnails_thumbnail_active_border_color": "#B08D57",
          },
        }}
      />
    </section>
  );
};

export default Gallery;
